import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  siftApiClient,
  type CacheDataset,
  type CacheDatasetGroup,
  type CacheRefreshJob,
  type SalesPlanBatchJob,
} from '../../api/siftApiClient';

/** How often to re-check a running refresh job. */
const JOB_POLL_MS = 2500;
/** How often to look for a build nobody here started (schedule, deploy). */
const IDLE_POLL_MS = 15000;

/** Split an ISO timestamp into a short relative headline + absolute local detail. */
function formatWhen(iso: string | null, emptyLabel = 'Never (this run)'): { rel: string; abs: string | null } {
  if (!iso) return { rel: emptyLabel, abs: null };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { rel: iso, abs: null };
  const secs = Math.max(0, (Date.now() - d.getTime()) / 1000);
  let rel: string;
  if (secs < 60) rel = 'just now';
  else if (secs < 3600) rel = `${Math.floor(secs / 60)}m ago`;
  else if (secs < 86400) rel = `${Math.floor(secs / 3600)}h ago`;
  else rel = `${Math.floor(secs / 86400)}d ago`;
  return { rel, abs: d.toLocaleString() };
}

/** One labelled freshness stat inside a dataset row. */
const Stat: React.FC<{ label: string; value: string; detail?: string | null; hint?: string }> = ({
  label, value, detail, hint,
}) => (
  <div className="cache-ds-stat" title={hint}>
    <div className="cache-ds-stat-label">{label}</div>
    <div className="cache-ds-stat-value">{value}</div>
    {detail && <div className="cache-ds-stat-detail glass-text-secondary">{detail}</div>}
  </div>
);

const STEP_ICON: Record<string, string> = { pending: '·', running: '', done: '✓', failed: '✕' };

/**
 * Admin "Data Refresh" tab. Lists the cron-refreshed Snowflake-backed datasets
 * grouped by refresh group — datasets inside a group are built on top of each
 * other, so they are shown (and refreshed) in step order. Each row shows both
 * freshness clocks: when SIFT last pulled the data, and the upstream Snowflake
 * ingest timestamp that data carries (the "data last updated" value in the app).
 *
 * A refresh runs for minutes, so refreshes are started as a background JOB whose
 * progress is polled — holding the HTTP request open used to time out and report
 * a failure while the backend was still happily refreshing.
 * Admin-gated at the route; this tab is only shown to admins.
 */
export const CacheRefreshSection: React.FC = () => {
  const [groups, setGroups] = useState<CacheDatasetGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [job, setJob] = useState<CacheRefreshJob | null>(null);
  /** Datasets being built right now by anyone: schedule, deploy, or a job. */
  const [building, setBuilding] = useState<string[]>([]);
  const [starting, setStarting] = useState(false);
  const [planBusy, setPlanBusy] = useState<'next' | 'current' | 'rebuild' | null>(null);
  const [planJob, setPlanJob] = useState<SalesPlanBatchJob | null>(null);
  const [planPending, setPlanPending] = useState(0);
  /** Tracks the previous job status so we only react to a finish once. */
  const wasRunning = useRef(false);

  const running = job?.status === 'running';
  const locked = running || starting;
  const anyBuilding = building.length > 0;
  const wasBuilding = useRef(false);
  // A run started in another tab, or by another admin, must disable these too --
  // hence the job status and not just this component's own click.
  const planRunning = planBusy !== null || planJob?.status === 'running';

  /** Dataset key -> label, so cross-group prerequisites render with a friendly name. */
  const labelsByKey = useMemo(() => {
    const map: Record<string, string> = {};
    groups.forEach((g) => g.datasets.forEach((d) => { map[d.key] = d.label; }));
    return map;
  }, [groups]);

  /** Dataset key -> when it was last built, for the out-of-order check below. */
  const refreshedByKey = useMemo(() => {
    const map: Record<string, number> = {};
    groups.forEach((g) => g.datasets.forEach((d) => {
      const t = d.last_refreshed_at ? Date.parse(d.last_refreshed_at) : NaN;
      if (!Number.isNaN(t)) map[d.key] = t;
    }));
    return map;
  }, [groups]);

  /**
   * Dependencies rebuilt more recently than the dataset derived from them.
   *
   * A derived dataset is only as current as what it was built FROM, and the
   * groups can be refreshed independently — so this is how you see that e.g.
   * buyer matches predate the vehicle data they were computed against.
   */
  const staleAgainst = useCallback((ds: CacheDataset): string[] => {
    const built = refreshedByKey[ds.key];
    if (built === undefined) return [];
    // A dependency that has never been built is absent from the map, and so
    // compares false here -- never built is not the same as newer.
    return ds.depends_on.filter((dep) => refreshedByKey[dep] > built);
  }, [refreshedByKey]);

  /** Dataset key -> its step in the running job, for per-row progress. */
  const activeSteps = useMemo(() => {
    const map: Record<string, CacheRefreshJob['steps'][number]> = {};
    if (running) job?.steps.forEach((s) => { map[s.dataset] = s; });
    return map;
  }, [job, running]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await siftApiClient.listCacheDatasets();
      setGroups(resp.groups);
    } catch {
      setError('Could not load dataset status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Poll job progress and in-flight builds: quickly while anything is running, slowly
  // otherwise, so a scheduled or post-deploy refresh shows up without a reload.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const resp = await siftApiClient.getCacheRefreshJob();
        if (cancelled) return;
        setJob(resp.job);
        setBuilding(resp.building ?? []);
      } catch {
        /* transient poll failure — keep the last known state */
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), running || anyBuilding ? JOB_POLL_MS : IDLE_POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [running, anyBuilding]);

  // A build nobody here started just finished: pull fresh timestamps.
  useEffect(() => {
    if (anyBuilding) { wasBuilding.current = true; return; }
    if (!wasBuilding.current || running) return;
    wasBuilding.current = false;
    void load();
  }, [anyBuilding, running, load]);

  // Same shape for the plan batch: it outlives the request that starts it, so the
  // panel polls rather than waits, and picks up a run started elsewhere on mount.
  // Keeps polling while writes are still draining -- "generated" is not "saved".
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { job: next, pending_writes: pending } = await siftApiClient.getSalesPlanJob();
        if (cancelled) return;
        setPlanJob(next);
        setPlanPending(pending ?? 0);
        if (next?.status !== 'running') setPlanBusy(null);
      } catch {
        /* transient poll failure — keep the last known state */
      }
    };
    void poll();
    if (planJob?.status !== 'running' && !planBusy && planPending === 0) {
      return () => { cancelled = true; };
    }
    const id = window.setInterval(() => void poll(), JOB_POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [planJob?.status, planBusy, planPending]);

  // When a job finishes, summarise the outcome and pull fresh timestamps.
  useEffect(() => {
    if (running) { wasRunning.current = true; return; }
    if (!wasRunning.current || !job) return;
    wasRunning.current = false;
    const failed = job.steps.filter((s) => s.status === 'failed');
    if (failed.length > 0 || job.status === 'failed') {
      setError(`${job.label}: failed at ${failed.map((f) => `step ${f.step} (${f.label})`).join(', ') || 'startup'}.`);
      setNote(null);
    } else {
      const rows = job.steps.reduce((sum, s) => sum + (s.row_count ?? 0), 0);
      setNote(`${job.label} refreshed — ${job.steps.length} step${job.steps.length === 1 ? '' : 's'}${rows ? `, ${rows.toLocaleString()} rows` : ''}.`);
      setError(null);
    }
    void load();
  }, [running, job, load]);

  const start = async (scope: string, label: string) => {
    setStarting(true);
    setNote(null);
    setError(null);
    try {
      const resp = await siftApiClient.startCacheRefresh(scope);
      setJob(resp.job);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 409
        ? 'A data refresh is already running — wait for it to finish.'
        : `Could not start the refresh for ${label}.`);
    } finally {
      setStarting(false);
    }
  };

  const generate = async (week: 'next' | 'current', rebuild = false) => {
    if (rebuild && !window.confirm(
      'Rebuild next week for EVERY rep with the current planner? Each rep\'s frozen week is '
      + 'replaced as the plan of record (their edits are kept). Nobody is emailed.',
    )) return;
    setPlanBusy(rebuild ? 'rebuild' : week);
    setNote(null);
    setError(null);
    try {
      setPlanJob((await (rebuild
        ? siftApiClient.generateSalesPlans(week, true)
        : siftApiClient.generateSalesPlans(week))).job);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Could not start plan generation.');
      setPlanBusy(null);
    }
  };

  return (
    <div className="cache-refresh-section">
      <div className="cache-refresh-head">
        <h3 className="glass-text-primary section-title">🗄️ Data Refresh</h3>
        <div className="cache-refresh-head-actions">
          <button className="glass-button" onClick={() => void load()} disabled={loading || locked}>
            ↻ Reload status
          </button>
          <button
            className="glass-button cache-refresh-all"
            onClick={() => void start('all', 'all datasets')}
            disabled={locked}
          >
            {job?.scope === 'all' && running ? 'Refreshing all…' : 'Refresh all'}
          </button>
        </div>
      </div>

      <p className="cache-refresh-intro glass-text-secondary">
        These datasets are rebuilt from Snowflake on a schedule (crons). Force a refresh here to
        pull in a fresh upstream load without waiting for the next scheduled run. Datasets are
        grouped by what is built from what — a group refreshes as a set, in the numbered step
        order. Each refresh swaps atomically, so the app keeps serving current data until the new
        data lands. Refreshes run in the background and take several minutes; progress is shown
        below, and you can leave this tab while one runs.
      </p>
      <p className="cache-refresh-intro glass-text-secondary">
        <strong>Last refreshed in SIFT</strong> is when this app last pulled the data.{' '}
        <strong>Source last refreshed</strong> is the upstream Snowflake load timestamp the current
        data carries — the value surfaced as “data last updated” in the app.
      </p>

      {running && job && (
        <div className="cache-refresh-progress" role="status" aria-live="polite">
          <div className="cache-progress-head">
            <span className="cache-spinner" aria-hidden="true" />
            <span className="cache-progress-title">
              Refreshing {job.label}… step{' '}
              {Math.min(job.steps.filter((s) => s.status !== 'pending').length || 1, job.steps.length)} of{' '}
              {job.steps.length}
            </span>
            <span className="cache-progress-since glass-text-secondary">
              started {formatWhen(job.started_at).rel}
            </span>
          </div>
          <ul className="cache-progress-steps">
            {job.steps.map((s) => (
              <li key={s.dataset} className={`cache-progress-step is-${s.status}`}>
                {s.status === 'running'
                  ? <span className="cache-spinner is-small" aria-hidden="true" />
                  : <span className="cache-progress-icon" aria-hidden="true">{STEP_ICON[s.status]}</span>}
                <span>{s.step}. {s.label}{s.status === 'pending' ? ' — queued' : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <div className="cache-refresh-error">{error}</div>}
      {note && <div className="cache-refresh-note">{note}</div>}

      {loading ? (
        <div className="cache-refresh-loading glass-text-secondary">Loading dataset status…</div>
      ) : (
        groups.map((group) => (
          <section className="cache-refresh-group" key={group.key}>
            <header className="cache-refresh-group-head">
              <div className="cache-group-heading">
                <h4 className="cache-group-title glass-text-primary">{group.label}</h4>
                <p className="cache-group-desc glass-text-secondary">{group.description}</p>
              </div>
              <button
                className="glass-button cache-group-refresh"
                onClick={() => void start(group.key, group.label)}
                disabled={locked}
              >
                {job?.scope === group.key && running
                  ? 'Refreshing group…'
                  : `Refresh group (${group.datasets.length} steps)`}
              </button>
            </header>

            <ol className="cache-ds-list">
              {group.datasets.map((ds) => {
                const stale = staleAgainst(ds);
                const sift = formatWhen(ds.last_refreshed_at);
                const source = formatWhen(ds.source_last_refreshed_at, 'Unknown');
                const active = activeSteps[ds.key];
                const isBuilding = active?.status === 'running' || building.includes(ds.key);
                const queued = active?.status === 'pending' && !isBuilding;
                return (
                  <li className="cache-ds-row" key={ds.key}>
                    {isBuilding ? (
                      <span className="cache-spinner" title="Refreshing now" />
                    ) : (
                      <span className="cache-ds-step" title={`Step ${ds.step} of ${ds.step_count}`}>
                        {ds.step}
                      </span>
                    )}

                    <div className="cache-ds-main">
                      <div className="cache-ds-label">
                        {ds.label}
                        {!ds.exists && <span className="cache-ds-missing" title="Not built yet">not built</span>}
                        {isBuilding && <span className="cache-ds-state is-building">Refreshing</span>}
                        {queued && job && (
                          <span className="cache-ds-state" title={`Runs as step ${active.step} of ${job.label}`}>
                            Queued · {active.step} of {job.steps.length}
                          </span>
                        )}
                      </div>
                      <div className="cache-ds-desc glass-text-secondary">{ds.description}</div>
                      {ds.depends_on.length > 0 && (
                        <div className="cache-ds-depends glass-text-secondary">
                          ⤷ Built from {ds.depends_on.map((dep) => labelsByKey[dep] ?? dep).join(' + ')}
                        </div>
                      )}
                      {stale.length > 0 && (
                        <div className="cache-ds-stale" role="status">
                          ⚠ Older than {stale.map((dep) => labelsByKey[dep] ?? dep).join(' + ')} — rebuild
                          this to pick up that data.
                        </div>
                      )}
                    </div>

                    <div className="cache-ds-meta">
                      <Stat label="Cadence" value={ds.cadence} />
                      <Stat label="Last refreshed in SIFT" value={sift.rel} detail={sift.abs} />
                      {ds.computed ? (
                        <Stat
                          label="Source last refreshed"
                          value="Derived"
                          hint="Computed locally from other datasets — no upstream load timestamp."
                        />
                      ) : (
                        <Stat label="Source last refreshed" value={source.rel} detail={source.abs} />
                      )}
                    </div>

                    {ds.individually_refreshable && (
                      <div className="cache-ds-action">
                        <button
                          className="glass-button"
                          onClick={() => void start(ds.key, ds.label)}
                          disabled={locked}
                        >
                          {isBuilding ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        ))
      )}

      <section className="cache-refresh-group">
        <header className="cache-refresh-group-head">
          <div className="cache-group-heading">
            <h4 className="cache-group-title glass-text-primary">Plan My Week</h4>
            <p className="cache-group-desc glass-text-secondary">
              Builds a week for every CSE territory and freezes it as the plan of record —
              the Friday pre-build, on demand. It never emails anyone, so it is safe to run
              repeatedly. A week that is already frozen is counted as skipped rather than
              rebuilt: the recommendation is written once so adherence keeps a stable
              baseline. <strong>Rebuild next week</strong> is the exception, for a release that
              lands after Friday&apos;s 06:00 pre-build: it rebuilds every rep&apos;s frozen next
              week with the current planner and keeps their edits.
            </p>
          </div>
          <div className="cache-plan-actions">
            <button
              className="glass-button"
              onClick={() => void generate('next')}
              disabled={planRunning || locked}
            >
              {planBusy === 'next' ? 'Generating…' : 'Generate next week'}
            </button>
            <button
              className="glass-button"
              onClick={() => void generate('current')}
              disabled={planRunning || locked}
            >
              {planBusy === 'current' ? 'Generating…' : 'Generate this week'}
            </button>
            <button
              className="glass-button"
              onClick={() => void generate('next', true)}
              disabled={planRunning || locked}
            >
              {planBusy === 'rebuild' ? 'Rebuilding…' : 'Rebuild next week'}
            </button>
          </div>
        </header>

        {planJob && (
          <div className="cache-plan-result glass-text-secondary" role="status" aria-live="polite">
            <strong>Week of {planJob.plan_week_start}</strong>
            {planJob.rebuild && ' (rebuild)'}
            {planJob.status === 'running' && ' — building every territory…'}
            {planJob.status === 'failed' && ` — failed: ${planJob.error ?? 'unknown error'}`}
            {planJob.status === 'done' && planJob.report && (
              <>
                {' — '}
                {planJob.report.generated.toLocaleString()} generated,{' '}
                {planJob.report.rebuilt ? `${planJob.report.rebuilt.toLocaleString()} rebuilt, ` : ''}
                {planJob.report.skipped.toLocaleString()} skipped,{' '}
                {planJob.report.failed.toLocaleString()} failed
                {planJob.report.duration_seconds != null
                  && ` in ${planJob.report.duration_seconds}s`}
              </>
            )}
            {planJob.report?.errors?.length ? (
              <ul className="cache-plan-errors">
                {planJob.report.errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            ) : null}
            {planJob.status === 'done' && (
              <div className="cache-plan-writes">
                {planPending > 0
                  ? `Saving to Snowflake — ${planPending.toLocaleString()} still to write…`
                  : 'All plans saved to Snowflake.'}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};
